import Const from 'const';
import Action from 'actions';
import {
    parseContentByCells,
    extractLateswapCsvLineups,
    makePlayersSnippet,
    assertPlayersSnippetFormatIsValid,
    getInvalidPlayerProps,
} from './helpers';
import FetchWrapper from 'helpers/fetch-wrapper';
import API from 'services/url/api-manager';
import SimplexLineup from 'models/simplex-lineup';
import {getSimplexUnitName} from 'helpers/simplex-helper';
import LateswapCsvLineupInterface from 'src/components/organisms/late-swap/services/lineup-view/lateswap-csv-lineup-interface';
import getLineupResolver from 'services/lineup-resolver/factory';
import {showComponentInPopup, showPopup} from 'helpers/modal';
import AlertableError from 'errors/alertable-error';
import SnippetIsInvalidError from './services/snippet-validators/snippet-is-invalid-error';
import {PlayerLineupsUsage} from 'value-objects/player-lineups-usage';
import {groupObjectsByProp} from 'helpers/array-helper';
import {setGameDetailsToPlayer} from 'helpers/player-helper';
import PlayerInterface from 'interfaces/players-data-interfaces/player-interface';
import {InvalidPlayerInterface, InvalidPlayer} from './services/invalid-player';
import {InvalidPlayers} from './components/invalid-players';

const LateSwap = {
    useLateSwap(use) {
        return {
            type: Const.LATESWAP_SET_IS_USED,
            use,
        };
    },

    useGroupingSameLateSwapLineups(useGrouping) {
        return {
            type: Const.LATESWAP_GROUP_SAME_LINEUPS,
            useGrouping,
        };
    },

    setUploadedCsvData(csvData) {
        return {
            type: Const.LATESWAP_SET_UPLOADED_CSV_DATA,
            csvData,
        };
    },

    setLateswapCsvLineups(lineups) {
        return {
            type: Const.LATESWAP_SET_CSV_LINEUPS,
            lineups,
        };
    },

    setLateswapSimplexLineups(lineups) {
        return {
            type: Const.LATESWAP_SET_SIMPLEX_LINEUPS,
            lineups,
        };
    },

    setLateswapLineupViews(lineups) {
        return {
            type: Const.LATESWAP_SET_LINEUP_VIEWS,
            lineups,
        };
    },

    setLateswapLineupsUsedPlayers(players) {
        return {
            type: Const.LATESWAP_SET_LINEUPS_USED_PLAYERS,
            players,
        };
    },

    setLateswapLoadingStatusNotStarted() {
        return {
            type: Const.LATESWAP_SET_LOADING_STATUS,
            status: Const.LATESWAP_LOADING_STATUS_NOT_STARTED,
        };
    },

    setLateswapLoadingStatusInProgress() {
        return {
            type: Const.LATESWAP_SET_LOADING_STATUS,
            status: Const.LATESWAP_LOADING_STATUS_IN_PROGRESS,
        };
    },

    setLateswapLoadingStatusFinished() {
        return {
            type: Const.LATESWAP_SET_LOADING_STATUS,
            status: Const.LATESWAP_LOADING_STATUS_FINISHED,
        };
    },

    processFileData(fileData) {
        return (dispatch, getState) => {
            const {sportConfig} = getState();

            const parsedContentByCells = parseContentByCells(fileData, sportConfig);

            dispatch(this.setLateswapLoadingStatusInProgress());

            dispatch(this.setUploadedCsvData(parsedContentByCells));

            dispatch(this.processFilePlayers())
                .then(() => {
                    dispatch(this.processFileLineups());
                    dispatch(this.setLateswapLoadingStatusFinished());
                })
                .catch((error) => {
                    dispatch(this.setLateswapLoadingStatusNotStarted());

                    if (error instanceof SnippetIsInvalidError) {
                        console.error(error);
                        return dispatch(showPopup(Const.LATESWAP_INCORRECT_CSV_FORMAT_MESSAGE));
                    }

                    if (error instanceof AlertableError) {
                        return dispatch(showPopup(error.message));
                    }

                    console.error(error);
                });
        };
    },

    processFilePlayers() {
        return (dispatch, getState) => {
            const {
                sportConfig,
                lateSwap: {uploadedCsvData},
            } = getState();

            const playersSnippet = makePlayersSnippet(uploadedCsvData.getAllAsArr(), sportConfig);

            try {
                assertPlayersSnippetFormatIsValid(playersSnippet, sportConfig);
            } catch (e) {
                return Promise.reject(e);
            }

            const url = API.to('RESOLVE_PLAYERS', {
                sport: sportConfig.getSport(),
                site: sportConfig.getSite(),
                gameType: sportConfig.getGameType(),
            });

            return FetchWrapper.fetchPost(url, {data: playersSnippet}).then((response) => {
                const {data} = response;

                dispatch(Action.addGamesIfAbsent(data.games));
                dispatch(this.resolvePlayersIfAbsent(data.players));

                return Promise.resolve(data);
            });
        };
    },

    processFileLineups() {
        return (dispatch, getState) => {
            dispatch(Action.createPoolsOfPlayers({filterPlayersBeforePreparingPools: false}));
            dispatch(Action.beforeOptimization());

            const {
                sportConfig,
                lateSwap: {uploadedCsvData},
                playersData: {preparedPlayers},
            } = getState();
            const indexedPlayers = preparedPlayers.groupedBy.externalId();

            const lateswapCsvLineups: LateswapCsvLineupInterface[] = extractLateswapCsvLineups(
                uploadedCsvData.getAllAsArr(),
                sportConfig,
                indexedPlayers,
            );

            dispatch(this.setLateswapCsvLineups(lateswapCsvLineups));
            dispatch(this.prepareLineupsByUploadedLineups());
            dispatch(
                Action.setLineupsQuantity(lateswapCsvLineups.length, {
                    saveToStorage: false,
                    isResetAllowed: false,
                }),
            );
            dispatch(Action.setStatusOfLineupsIsFilled(true));
            dispatch(Action.createLineupPlayerMap());
        };
    },

    prepareLineupsByUploadedLineups() {
        return (dispatch, getState) => {
            const {
                sportConfig,
                lateSwap: {csvLineups},
            } = getState();
            const lineupResolver = getLineupResolver(sportConfig);

            const preparedLineups: object[] = [];
            const simplexLineups: object[] = [];
            csvLineups.getAllAsArr().map((lateswapCsvLineup, index) => {
                const lineupNumber = 1 + index;

                const uploadedPlayers = lateswapCsvLineup.getPlayers();

                let lineup = uploadedPlayers.map((uploadedPlayer) => uploadedPlayer.getPlayer());
                let simplexUnits = lineup.map((player) => getSimplexUnitName(player.playerId));

                const processData = lineupResolver.getResolvingProcessData(getState());

                // @ts-ignore
                const simplexLineup = new SimplexLineup(simplexUnits, processData);
                simplexLineups.push(simplexLineup);

                dispatch(Action.setRawLineup(simplexLineup));

                const preparedLineup = dispatch(
                    Action.do('createLineupView', lineup, lineupNumber, simplexLineup.getId()),
                );

                if (!isEmpty(preparedLineup)) {
                    preparedLineups.push(preparedLineup);
                }
            });

            dispatch(this.setLateswapSimplexLineups(simplexLineups));
            dispatch(this.setLateswapLineupViews(preparedLineups));
            dispatch(Action.setOptimizedLineupViews(preparedLineups));

            dispatch(Action.setLineupsUsedPlayers());
            dispatch(this.prepareUsedPlayersByUploadedLineups(preparedLineups));
        };
    },

    prepareUsedPlayersByUploadedLineups(lineups) {
        return (dispatch) => {
            const usedPlayers = dispatch(Action.calcLineupsUsedPlayers(lineups));

            usedPlayers.map((usedPlayer) => {
                usedPlayer.lateSwapLineupsUsage = new PlayerLineupsUsage(usedPlayer.usedLineupsCount, lineups.length);
            });

            dispatch(this.setLateswapLineupsUsedPlayers(usedPlayers));
        };
    },

    resolvePlayersIfAbsent(players = []) {
        return (dispatch, getState) => {
            const {playersData} = getState();
            const existingPlayers = playersData.players.all();
            const {games} = playersData;

            const groupedPlayers = groupObjectsByProp(existingPlayers, 'externalId');

            // @ts-ignore
            const absentPlayers = players.filter((player) => !(player.externalId in groupedPlayers));

            const resolvedPlayers: PlayerInterface[] = [];
            const invalidPlayers: InvalidPlayerInterface[] = [];
            absentPlayers.map((player: PlayerInterface) => {
                const invalidProps = getInvalidPlayerProps(player);

                if (invalidProps.length > 0) {
                    invalidPlayers.push(InvalidPlayer.create(player, invalidProps));
                    return;
                }

                if (!player.gameInfo) {
                    const game = games.getGameByTeamName(player.team);

                    if (game === null) {
                        invalidPlayers.push(InvalidPlayer.create(player, ['team']));
                        return;
                    }

                    setGameDetailsToPlayer(game, player);
                }

                resolvedPlayers.push(player);
            });

            dispatch(this.showMessageAboutInvalidPlayersIfPresent(invalidPlayers));

            dispatch(Action.addAbsentPlayersToPool(resolvedPlayers));
        };
    },

    showMessageAboutInvalidPlayersIfPresent(invalidPlayers) {
        return (dispatch) => {
            if (isEmpty(invalidPlayers)) {
                return;
            }

            dispatch(
                showComponentInPopup(InvalidPlayers, {invalidPlayers}, {
                    useConfirm: true,
                    useClose: false,
                    type: 'details',
                }),
            );
        };
    },
};

export default {...Action, ...LateSwap};
